import { IReminderData } from 'data-ng-personal-reminders';
import { IStaffDrivenData } from 'data-ng-staff-todos';
import * as _4 from 'lodash4';
import * as moment from 'moment';
import { EnumeratedSet, IEnumeratedSetValueDatatype } from 'utility-js-enumeration';
import { TypeUtility } from 'utility-ts-type';
import { PathItem } from './../builders/path_item_builder';
import { PathTask } from './../builders/path_task_builder';
import { TimeFormatterFunction } from './time_formatter_fn';

export class PathTaskFormatter {
  constructor(
    private pathTaskSecondaryTextEnum: EnumeratedSet,
    private ItemPresenterEnum: any,
    private taskUpdateFn: any,
    private ngTypeUtility: typeof TypeUtility
  ) {}

  private isAllDayEvent(task: PathTask): boolean {
    return !_4.isEmpty(task.start_ts) && task.start_ts.isSame(task.end_ts, 'day');
  }

  private tsIsValid(date: moment.Moment): boolean {
    return !_4.isEmpty(date) && date.isValid();
  }

  private getBeginningOfDateRange(task: PathTask, isAx: boolean): string {
    return !this.tsIsValid(task.start_ts)
      ? this.getStartDateMessage(task, isAx)
      : `${this.getStartDateMessage(task, isAx)} to `;
  }

  private isToday(date: moment.Moment): boolean {
    const today = moment();
    return date.isAfter(today.startOf('day')) && date.isBefore(today.endOf('day'));
  }

  private getDateMarkerText(task: PathTask, isAx: boolean): string {
    if (this.isAllDayEvent(task)) {
      return `${this.getStartDateMessage(task, isAx)}`;
    }
    return `${this.getBeginningOfDateRange(task, isAx)}${this.getEndDateMessage(task, isAx)}`;
  }

  private getAutoCompleteText(task: PathTask, isAx: boolean): string {
    let textDisplay: string = this.pathTaskSecondaryTextEnum.get('auto_complete_active')!.value.secondaryText;
    const isTaskCompleted: boolean = this.taskUpdateFn.isCompleted(task);
    if (!isTaskCompleted && this.taskUpdateFn.isPendingComplete(task)) {
      textDisplay = this.pathTaskSecondaryTextEnum.get('auto_complete_pending')!.value.secondaryText;
    } else if (isTaskCompleted) {
      textDisplay = this.pathTaskSecondaryTextEnum.get('auto_completed')!.value.secondaryText;
    }

    if (isTaskCompleted || this.taskUpdateFn.isPendingComplete(task)) {
      return `${textDisplay}${this.getChangedTime(task, isAx)}`;
    } else {
      return `${textDisplay}${this.getDateMarkerText(task, isAx)}`;
    }
  }

  getReminderText(task: IReminderData, isAx: boolean = false) {
    const dateTime = moment(`${task.due_dt} ${task.due_tm}`);
    const noTime = dateTime.hours() === 0 && dateTime.minutes() === 0;
    const dueToday = moment().isSame(dateTime, 'day');
    if (dueToday && noTime) {
      return 'Due today';
    }

    if (dueToday) {
      return `Due today at ${dateTime.format('h:mm a')}`;
    }

    if (isAx && noTime) {
      return `Due on ${TimeFormatterFunction.monthAndDateAndYear(dateTime)}`;
    }

    if (isAx) {
      return `Due on ${TimeFormatterFunction.monthAndDateAndYear(dateTime)} at ${dateTime.format('h:mm a')}`;
    }

    if (noTime) {
      return `Due on ${this.ngTypeUtility.time.getWeekDayMonthDay(dateTime)}`;
    }

    return `Due on ${this.ngTypeUtility.time.getWeekDayMonthDay(dateTime)} at ${dateTime.format('h:mm a')}`;
  }

  getStaffTodoText(task: IStaffDrivenData, isOverdue: boolean, isAx: boolean = false) {
    const dueDate = moment(`${task.due_date}`);
    if (task.cleared_date) {
      return `Completed on ${this.ngTypeUtility.time.getWeekDayMonthDay(moment(`${task.cleared_date}`))}`;
    }
    if (moment().isSame(dueDate, 'day')) {
      return 'Due today';
    }
    if (isOverdue) {
      return `Overdue on ${this.ngTypeUtility.time.getWeekDayMonthDay(dueDate)}`;
    }
    if (isAx) {
      return `Due on ${TimeFormatterFunction.monthAndDateAndYear(dueDate)}`;
    }
    return `Due on ${this.ngTypeUtility.time.getWeekDayMonthDay(dueDate)}`;
  }

  private getOverdueText(task: PathTask, isAx: boolean = false): string {
    return `${this.pathTaskSecondaryTextEnum.get('overdue')!.value.secondaryText}${this.getEndDateMessage(task, isAx)}`;
  }

  getPathItemDateText(
    task: PathTask | IReminderData | IStaffDrivenData,
    isOverdue: boolean,
    isAx: boolean = false
  ): string {
    if (this.taskUpdateFn.isPersonalReminder(task)) {
      return this.getReminderText(<IReminderData>task, isAx);
    }

    if (this.taskUpdateFn.isStaffDrivenToDo(task)) {
      return this.getStaffTodoText(<IStaffDrivenData>task, isOverdue, isAx);
    }

    if (isOverdue) {
      return this.getOverdueText(<PathTask>task, isAx);
    }

    if (this.isDateMarkerCategory(<PathTask>task)) {
      return this.getDateMarkerText(<PathTask>task, isAx);
    }

    if (this.taskUpdateFn.isAutoCompletable(<PathTask>task)) {
      return this.getAutoCompleteText(<PathTask>task, isAx);
    }

    if (!this.taskUpdateFn.isActive(task)) {
      return `${this.pathTaskSecondaryTextEnum.get(task.status)!.value.secondaryText}${this.getChangedTime(
        <PathTask>task,
        isAx
      )}`;
    }

    if (
      !_4.isEmpty((<PathTask>task).start_ts) &&
      (<PathTask>task).start_ts.diff((<PathTask>task).end_ts, 'days') === 0
    ) {
      return this.getEndDateMessage(task, isAx);
    }

    return `${this.getBeginningOfDateRange(<PathTask>task, isAx)}${this.getEndDateMessage(<PathTask>task, isAx)}`;
  }

  getStartDateMessage(task: PathTask, isAx: boolean = false): string {
    const tsExists = this.tsIsValid(task.start_ts);
    if (tsExists && this.isToday(task.start_ts)) {
      return 'Today';
    }

    if (!tsExists) {
      return this.pathTaskSecondaryTextEnum.get(task.status)!.value.secondaryText;
    }

    if (isAx) {
      return TimeFormatterFunction.monthAndDateAndYear(task.start_ts);
    }

    return TimeFormatterFunction.defaultTime(task.start_ts);
  }

  getToDoTitle(task: PathTask, item: PathItem): string {
    const substitute_text = _4.get(task, 'substitute_text_by_field_by_item', {});
    if (substitute_text[item.id]) {
      return substitute_text[item.id].title;
    }
    return item.title;
  }

  getEndDateMessage(task: any, isAx: boolean = false): string {
    const tsExists = this.tsIsValid(task.end_ts);
    if (tsExists && this.isToday(task.end_ts)) {
      return 'Today';
    }
    if (isAx) {
      return tsExists
        ? TimeFormatterFunction.monthAndDateAndYear(task.end_ts)
        : TimeFormatterFunction.monthAndDateAndYear(task.due_ts);
    }
    return tsExists ? TimeFormatterFunction.defaultTime(task.end_ts) : TimeFormatterFunction.defaultTime(task.due_ts);
  }

  getChangedTime(task: PathTask, isAx: boolean): string {
    if (_4.isEmpty(task.status_changed_ts) && isAx) {
      return TimeFormatterFunction.monthAndDateAndYear(moment());
    }
    if (isAx) {
      return TimeFormatterFunction.monthAndDateAndYear(moment(task.status_changed_ts as string));
    }
    if (_4.isEmpty(task.status_changed_ts)) {
      return TimeFormatterFunction.defaultTime(moment());
    }
    // Moment accepts both string and Moment objects.
    return TimeFormatterFunction.defaultTime(moment(task.status_changed_ts as string));
  }

  isDateMarkerCategory(task: PathTask): boolean {
    return task.category && task.category.isEqual('date_marker');
  }

  isFullDayEvent(task: PathTask): boolean {
    return (
      TimeFormatterFunction.defaultTime(task.start_ts) === TimeFormatterFunction.defaultTime(task.end_ts) &&
      task.category &&
      task.category.isEqual('date_marker')
    );
  }

  isAppointment(task: PathTask, item: PathItem): boolean {
    return (
      !this.taskUpdateFn.isPersonalReminder(task) &&
      !this.taskUpdateFn.isStaffDrivenToDo(task) &&
      task.category &&
      task.category.isEqual('date_marker') &&
      this.ItemPresenterEnum.isAppointment(item.presenter)
    );
  }

  isAppointmentRequest(task: PathTask, item: PathItem): boolean {
    return (
      !this.taskUpdateFn.isPersonalReminder(task) &&
      !this.taskUpdateFn.isStaffDrivenToDo(task) &&
      (this.ItemPresenterEnum.isAppointmentRequest(item.presenter) ||
        this.ItemPresenterEnum.isAppointmentInvitation(item.presenter))
    );
  }

  isArticle(task: PathTask, item: PathItem): boolean {
    return (
      !this.taskUpdateFn.isPersonalReminder(task) &&
      !this.taskUpdateFn.isStaffDrivenToDo(task) &&
      this.ItemPresenterEnum.isArticle(item.presenter)
    );
  }

  getCalendarDay(task: PathTask): string {
    return !this.hasValidStartDate(task) ? task.end_ts.format('D') : task.start_ts.format('D');
  }

  getCalendarMonth(task: PathTask): string {
    return !this.hasValidStartDate(task) ? task.end_ts.format('MMM') : task.start_ts.format('MMM');
  }

  isPlacementTest(task: PathTask, item: PathItem): boolean {
    return (
      !this.taskUpdateFn.isPersonalReminder(task) &&
      !this.taskUpdateFn.isStaffDrivenToDo(task) &&
      this.ItemPresenterEnum.isPlacementtest(item.presenter)
    );
  }

  hasValidStartDate(task: PathTask): boolean {
    return !_4.isUndefined(task.start_ts) && task.start_ts.isValid();
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-todos/src/data/functions/path_task_formatter_fn.ts